/**
 * The application, drawn in enough detail to be believed — and usable.
 *
 * It was a still picture at the top of the page, which is a strange thing to
 * put above a product whose whole argument is "you can see all of it before you
 * upload anything". The sidebar works now: pick a view, pick a trip, and the
 * grid changes. It is the first thing on the page and the first thing that
 * responds to a click.
 *
 * What makes software look real is density — small text, file names, byte
 * counts, controls that look like they do something specific. Nobody faking it
 * bothers with "1.93 GB of 2 GB", so putting it there is itself a signal, and
 * here it costs nothing because those are the strings the real interface shows.
 *
 * Motion is framer-motion's `layout`: when the grid changes, tiles that
 * survive the change *move* to their new places rather than blinking out and
 * back somewhere else. That is the one thing CSS cannot do without measuring
 * elements by hand, and the reason the dependency is here at all.
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { type Locale, useLocale, useT } from "../../lib/i18n.js";
import { plural, type PluralForms } from "../../lib/plural.js";
import "./photoTiles.css";
import "./AppWindow.css";

const VIEWS = [
  "All photos",
  "Videos",
  "Albums",
  "People",
  "Favourites",
  "Trash",
] as const;
type View = (typeof VIEWS)[number];

interface Trip {
  name: string;
  count: number;
  /** Which drawn photographs this trip is made of. */
  kinds: string[];
}

const TRIPS: Trip[] = [
  {
    name: "Croatia & Italy 2026",
    count: 342,
    kinds: [
      "landscape",
      "portrait",
      "sky",
      "forest",
      "sunset",
      "sea",
      "stone",
      "portrait-two",
      "landscape",
    ],
  },
  {
    name: "Slovenia",
    count: 128,
    kinds: [
      "forest",
      "sea",
      "landscape",
      "stone",
      "sky",
      "forest",
      "sunset",
      "sea",
      "portrait",
    ],
  },
  {
    name: "Alps, winter",
    count: 96,
    kinds: [
      "sky",
      "stone",
      "landscape",
      "sky",
      "portrait-two",
      "stone",
      "sea",
      "landscape",
      "sky",
    ],
  },
  {
    name: "Kasia’s wedding",
    count: 214,
    kinds: [
      "portrait",
      "portrait-two",
      "stone",
      "portrait",
      "sunset",
      "portrait-two",
      "sky",
      "portrait",
      "sea",
    ],
  },
];

/** Views that show something other than the full grid. */
const VIEW_FILTER: Partial<Record<View, string[]>> = {
  Videos: ["landscape", "sea", "sunset"],
  Favourites: ["sunset", "portrait", "sea", "landscape"],
  People: ["portrait", "portrait-two", "portrait", "portrait-two"],
  Trash: [],
};

/** "342 items", where Polish needs a different word at 5 than at 4. */
const ITEM_FORMS: Record<Locale, PluralForms> = {
  en: { one: "item", other: "items" },
  pl: { one: "element", few: "elementy", many: "elementów", other: "elementy" },
};

export function AppWindow() {
  const t = useT();
  const { locale } = useLocale();
  const [view, setView] = useState<View>("All photos");
  const [trip, setTrip] = useState(0);

  const current = TRIPS[trip]!;
  const kinds = VIEW_FILTER[view] ?? current.kinds;
  const showing = view === "All photos" ? current.count : kinds.length;

  return (
    <div className="fc-appwin">
      <div className="fc-appwin__frame">
        {/* ── Sidebar ─────────────────────────────────────────────────── */}
        <aside className="fc-appwin__side">
          <div className="fc-appwin__group">
            <span className="fc-appwin__group-mark">CW</span>
            <span className="fc-appwin__group-name">{t("Croatia 2026")}</span>
          </div>

          <nav className="fc-appwin__nav" aria-label={t("Library")}>
            {VIEWS.map((item) => (
              <button
                key={item}
                type="button"
                data-active={view === item || undefined}
                aria-pressed={view === item}
                onClick={() => setView(item)}
              >
                {t(item)}
              </button>
            ))}
          </nav>

          <div className="fc-appwin__albums">
            <span className="fc-appwin__label">{t("Trips")}</span>
            {TRIPS.map((item, index) => (
              <button
                key={item.name}
                type="button"
                className="fc-appwin__album"
                data-active={trip === index || undefined}
                aria-pressed={trip === index}
                onClick={() => {
                  setTrip(index);
                  setView("All photos");
                }}
              >
                <span className="fc-appwin__album-name">{t(item.name)}</span>
                <span className="fc-appwin__album-count fc-tabular">
                  {item.count}
                </span>
              </button>
            ))}
          </div>

          {/* The number that does more for credibility than any adjective. */}
          <div className="fc-appwin__storage">
            <span className="fc-appwin__meter">
              <span />
            </span>
            <span className="fc-appwin__storage-text">
              {t("1.93 GB of 2 GB")}
            </span>
          </div>
        </aside>

        {/* ── Main ────────────────────────────────────────────────────── */}
        <div className="fc-appwin__main">
          <div className="fc-appwin__bar">
            <span className="fc-appwin__search">{t("Search this group")}</span>
            <span className="fc-appwin__btn" data-primary>
              {t("Add photos")}
            </span>
          </div>

          <div className="fc-appwin__people">
            <span className="fc-appwin__label">{t("People")}</span>
            <span className="fc-appwin__faces">
              {["MK", "NW", "PJ", "TB", "JL"].map((initials, index) => (
                <span
                  key={initials}
                  className="fc-appwin__face"
                  data-face={index + 1}
                >
                  {initials}
                </span>
              ))}
            </span>
          </div>

          <div className="fc-appwin__head">
            <span className="fc-appwin__title">
              {view === "All photos" ? t(current.name) : t(view)}
            </span>
            <span className="fc-appwin__meta fc-tabular" aria-live="polite">
              {kinds.length === 0
                ? t("Nothing here")
                : `${String(showing)} ${plural(locale, showing, ITEM_FORMS[locale])} · ${t("1.93 GB")}`}
            </span>
          </div>

          {/*
            `layout` on each tile is the reason this uses framer-motion at all:
            switching trip keeps some tiles and replaces others, and the ones
            that stay slide to their new positions instead of blinking out and
            back somewhere else.
          */}
          <div className="fc-appwin__grid">
            {kinds.map((kind, index) => (
              <motion.span
                key={`${String(trip)}-${view}-${String(index)}`}
                layout
                className="fc-appwin__tile fc-phototile"
                data-kind={kind}
                /*
                 * Scale only, never opacity, and no exit.
                 *
                 * framer-motion applies `initial` immediately and moves to
                 * `animate` on the next animation frame. Where that frame
                 * never arrives — a hidden tab, a suspended renderer, a
                 * JavaScript failure — an initial opacity of 0 leaves the
                 * photographs permanently invisible. Starting at 0.9 scale
                 * means the worst case is tiles slightly too small, which
                 * nobody notices, rather than a grid that is not there.
                 *
                 * Exit animations are gone for the same reason: an exit that
                 * never completes never unmounts, and switching view twice
                 * left eighteen tiles stacked in the DOM.
                 */
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 420,
                  damping: 34,
                  delay: index * 0.022,
                }}
              />
            ))}
          </div>

          {kinds.length === 0 && (
            <p className="fc-appwin__empty">
              {t("Nothing in the trash. Deleted photos wait 30 days.")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
